import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import api from "@/lib/api";

/**
 * FoundingWall — auto-scrolling marquee of anonymized (first name + city)
 * founding members. Duplicates the list so the scroll loops seamlessly.
 * Data pulled from /api/launch/founding-wall (blends real signups with seed).
 */
export default function FoundingWall() {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/launch/founding-wall?limit=50")
      .then(r => { if (!cancelled) setMembers(r.data?.members || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (members.length === 0) return null;

  // Duplicate the list so translateX(-50%) loops seamlessly.
  const loop = [...members, ...members];

  return (
    <section
      className="bg-[var(--ff-primary)] text-white py-14 overflow-hidden"
      data-testid="founding-wall-section"
      aria-labelledby="founding-wall-heading"
    >
      <div className="max-w-7xl mx-auto px-6 text-center mb-10">
        <p className="overline text-[var(--ff-accent-kraft)] inline-flex items-center gap-1.5 justify-center">
          <Users size={12}/> The founding class
        </p>
        <h2
          id="founding-wall-heading"
          className="font-display text-3xl sm:text-4xl mt-3 leading-tight"
        >
          Real people. Building this with us.
        </h2>
      </div>

      {/* Marquee */}
      <div
        className="relative w-full overflow-hidden"
        data-testid="founding-wall-marquee"
        style={{
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
          maskImage:
            "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
        }}
      >
        <div
          className="flex gap-3 whitespace-nowrap"
          style={{
            animation: "founding-marquee 60s linear infinite",
            width: "max-content",
          }}
        >
          {loop.map((m, i) => (
            <span
              key={`${m.first_name}-${m.city}-${i}`}
              className="inline-flex items-baseline gap-2 bg-white/8 border border-white/15 rounded-full px-4 py-2 text-sm"
              data-testid={i < members.length ? `founding-wall-chip-${i}` : undefined}
            >
              <span className="font-semibold">{m.first_name}</span>
              <span className="text-white/60 text-xs">· {m.city}</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes founding-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
